import {
  css,
  DefaultTheme,
  FlattenSimpleInterpolation,
} from 'styled-components/macro';
import { ButtonMode } from './button.types';

import {
  counterStyles,
  focus,
  hover,
  selected,
  selectedHover,
} from './styled-mixins';

type ButtonColorsMap = Record<
  ButtonMode,
  Record<string, FlattenSimpleInterpolation>
>;

export const buttonColorsMap = ({
  colors,
  semantic,
}: DefaultTheme): ButtonColorsMap => ({
  accent: {
    main: css`
      background-color: ${semantic.action.accent.bg};
      color: ${semantic.text.onColored};
      ${counterStyles({
        color: semantic.action.accent.bg,
        bg: semantic.text.onColored,
      })}

      ${hover(css`
        background-color: ${semantic.action.accent.bgHover};
        ${counterStyles({
          color: semantic.action.accent.bgHover,
        })}
      `)}

      ${focus(semantic.focus.ringAccent)}
    `,
    outline: css`
      border-color: ${semantic.action.accent.border};
      background-color: ${semantic.action.accent.bgOutline};
      color: ${semantic.action.accent.textOnSurface};
      ${counterStyles({
        color: semantic.text.onColored,
        bg: semantic.action.accent.textOnSurface,
      })}

      ${hover(css`
        border-color: ${semantic.action.accent.borderHover};
        color: ${semantic.action.accent.textOnSurfaceHover};
        ${counterStyles({
          bg: semantic.action.accent.textOnSurfaceHover,
        })}
      `)}

      ${selected(css`
        background-color: ${semantic.action.accent.bg};
        color: ${semantic.text.onColored};
        ${counterStyles({
          color: semantic.action.accent.bg,
          bg: semantic.text.onColored,
        })}
      `)}

      ${selectedHover(css`
        background-color: ${semantic.action.accent.bgHover};
        color: ${semantic.text.onColored};
        ${counterStyles({
          color: semantic.action.accent.bg,
          bg: semantic.text.onColored,
        })}
      `)}

      ${focus(semantic.focus.ringAccent)}
    `,
    minor: css`
      background-color: ${semantic.action.accent.bgMinor};
      color: ${semantic.action.accent.textOnSurface};
      ${counterStyles({
        color: semantic.text.onColored,
        bg: semantic.action.accent.textOnSurface,
      })}

      ${hover(css`
        background-color: ${semantic.action.neutral.bgHover};
        ${counterStyles({
          color: semantic.action.neutral.bgHover,
        })}
      `)}

      ${selected(css`
        background-color: ${semantic.action.accent.bg};
        color: ${semantic.text.onColored};
        ${counterStyles({
          color: semantic.action.accent.bg,
          bg: semantic.text.onColored,
        })}
      `)}

      ${selectedHover(css`
        background-color: ${semantic.action.accent.bgHover};
        color: ${semantic.text.onColored};
        ${counterStyles({
          color: semantic.action.accent.bgHover,
          bg: semantic.text.onColored,
        })}
      `)}

      ${focus(semantic.focus.ringAccent)}
    `,
    ghost: css`
      background-color: transparent;
      color: ${semantic.action.accent.textOnSurface};
      ${counterStyles({
        color: semantic.text.onColored,
        bg: semantic.action.accent.textOnSurface,
      })}

      ${hover(css`
        color: ${semantic.action.accent.textOnSurfaceHover};
        ${counterStyles({
          bg: semantic.action.accent.textOnSurfaceHover,
        })}
      `)}

      ${selected(css`
        background-color: ${semantic.action.accent.bg};
        color: ${semantic.text.onColored};
        ${counterStyles({
          color: semantic.action.accent.bg,
          bg: semantic.text.onColored,
        })}
      `)}

      ${selectedHover(css`
        background-color: ${semantic.action.accent.bgHover};
        color: ${semantic.text.onColored};
        ${counterStyles({
          color: semantic.action.accent.bgHover,
          bg: semantic.text.onColored,
        })}
      `)}

      ${focus(semantic.focus.ringAccent)}
    `,
  },
  neutral: {
    main: css`
      background-color: ${semantic.action.neutral.bg};
      color: ${semantic.action.neutral.text};
      ${counterStyles({
        color: semantic.action.neutral.bg,
        bg: semantic.action.neutral.text,
      })}

      ${hover(css`
        background-color: ${semantic.action.neutral.bgHover};
        ${counterStyles({
          color: semantic.action.neutral.bgHover,
        })}
      `)};

      ${selected(css`
        background-color: ${semantic.action.neutral.bgSelected};
        ${counterStyles({
          color: semantic.action.neutral.bgSelected,
        })}
      `)}

      ${selectedHover(css`
        background-color: ${semantic.action.neutral.bgSelectedHover};
        ${counterStyles({
          color: semantic.action.neutral.bgSelectedHover,
        })}
      `)}

      ${focus(semantic.focus.ringAccent)}
    `,
    outline: css`
      border-color: ${semantic.border.control};
      background-color: ${semantic.action.neutral.bgOutline};
      color: ${semantic.action.neutral.text};
      ${counterStyles({
        color: semantic.action.neutral.bgOutline,
        bg: semantic.action.neutral.text,
      })};

      ${hover(css`
        background-color: ${semantic.action.neutral.bgOutlineHover};
        ${counterStyles({
          color: semantic.action.neutral.bgOutlineHover,
        })};
      `)}

      ${selected(css`
        background-color: ${semantic.action.neutral.bgSelected};
        ${counterStyles({
          color: semantic.action.neutral.bgSelected,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${semantic.action.neutral.bgSelectedHover};
        ${counterStyles({
          color: semantic.action.neutral.bgSelectedHover,
        })};
      `)}

      ${focus(semantic.focus.ringNeutral)}
    `,
    minor: css`
      border-color: ${semantic.border.control};
      background-color: ${semantic.action.neutral.bgMinor};
      color: ${semantic.action.neutral.text};
      ${counterStyles({
        color: semantic.action.neutral.bgMinor,
        bg: semantic.action.neutral.text,
      })};

      ${hover(css`
        background-color: ${semantic.action.neutral.bgHover};
        ${counterStyles({
          color: semantic.action.neutral.bgHover,
        })};
      `)}

      ${selected(css`
        background-color: ${semantic.action.neutral.bgSelected};
        ${counterStyles({
          color: semantic.action.neutral.bgSelected,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${semantic.action.neutral.bgSelectedHover};
        ${counterStyles({
          color: semantic.action.neutral.bgSelectedHover,
        })};
      `)}

      ${focus(semantic.focus.ringNeutral)}
    `,
    ghost: css`
      background-color: transparent;
      color: ${semantic.action.neutral.text};
      ${counterStyles({
        color: semantic.text.onColored,
        bg: semantic.action.neutral.text,
      })};

      ${hover(css`
        background-color: ${semantic.action.neutral.bgHover};
        ${counterStyles({
          color: semantic.action.neutral.bgHover,
        })};
      `)}

      ${selected(css`
        background-color: ${semantic.action.neutral.bgSelected};
        ${counterStyles({
          color: semantic.action.neutral.bgSelected,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${semantic.action.neutral.bgSelectedHover};
        ${counterStyles({
          color: semantic.action.neutral.bgSelectedHover,
        })};
      `)}

      ${focus(semantic.focus.ringNeutral)}
    `,
  },
  additional: {
    // TODO: нужны роли selected/selected-hover для additional.
    main: css`
      background-color: ${semantic.action.additional.bg};
      color: ${semantic.action.additional.text};
      ${counterStyles({
        color: semantic.action.additional.bg,
        bg: semantic.action.additional.text,
      })};

      ${hover(css`
        background-color: ${semantic.action.additional.bgHover};
        ${counterStyles({
          color: semantic.action.additional.bgHover,
        })};
      `)};

      ${selected(css`
        background-color: ${colors.primary[600]};
        color: ${semantic.text.onColored};
        ${counterStyles({
          color: colors.primary[600],
          bg: semantic.text.onColored,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.primary[700]};
        ${counterStyles({
          color: colors.primary[700],
        })};
      `)}

      ${focus(semantic.focus.ringNeutral)}
    `,
  },
  success: {
    main: css`
      background-color: ${semantic.action.success.bg};
      color: ${semantic.action.success.text};
      ${counterStyles({
        color: semantic.action.success.bg,
        bg: semantic.action.success.text,
      })};

      ${hover(css`
        background-color: ${semantic.action.success.bgHover};
        ${counterStyles({
          color: semantic.action.success.bgHover,
        })};
      `)}

      ${selected(css`
        background-color: ${semantic.action.success.bgSelected};
        color: ${semantic.text.onColored};
        ${counterStyles({
          color: semantic.action.success.bgSelected,
          bg: semantic.text.onColored,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${semantic.action.success.bgSelectedHover};
        ${counterStyles({
          color: semantic.action.success.bgSelectedHover,
        })};
      `)}

      ${focus(semantic.focus.ringNeutral)}
    `,
    ghost: css`
      background-color: transparent;
      color: ${semantic.action.success.text};
      ${counterStyles({
        color: semantic.text.onColored,
        bg: semantic.action.success.text,
      })};

      ${hover(css`
        background-color: ${semantic.action.neutral.bgHover};
        ${counterStyles({
          color: semantic.action.neutral.bgHover,
        })};
      `)}

      ${selected(css`
        background-color: ${semantic.action.success.bg};
        ${counterStyles({
          color: semantic.action.success.bg,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${semantic.action.success.bgHover};
        ${counterStyles({
          color: semantic.action.success.bgHover,
        })};
      `)}

      ${focus(semantic.focus.ringSuccess)}
    `,
  },
  warning: {
    main: css`
      background-color: ${semantic.action.warning.bg};
      color: ${semantic.action.warning.text};
      ${counterStyles({
        color: semantic.action.warning.bg,
        bg: semantic.action.warning.text,
      })};

      ${hover(css`
        background-color: ${semantic.action.warning.bgHover};
        ${counterStyles({
          color: semantic.action.warning.bgHover,
        })};
      `)}

      ${selected(css`
        background-color: ${semantic.action.warning.bgSelected};
        color: ${semantic.text.onColored};
        ${counterStyles({
          color: semantic.action.warning.bgSelected,
          bg: semantic.text.onColored,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${semantic.action.warning.bgSelectedHover};
        ${counterStyles({
          color: semantic.action.warning.bgSelectedHover,
        })};
      `)}

      ${focus(semantic.focus.ringNeutral)}
    `,
    ghost: css`
      background-color: transparent;
      color: ${semantic.action.warning.text};
      ${counterStyles({
        color: semantic.text.onColored,
        bg: semantic.action.warning.text,
      })};

      ${hover(css`
        background-color: ${semantic.action.neutral.bgHover};
        ${counterStyles({
          color: semantic.action.neutral.bgHover,
        })};
      `)}

      ${selected(css`
        background-color: ${semantic.action.warning.bg};
        ${counterStyles({
          color: semantic.action.warning.bg,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${semantic.action.warning.bgHover};
        ${counterStyles({
          color: semantic.action.warning.bgHover,
        })};
      `)}

      ${focus(semantic.focus.ringWarning)}
    `,
  },
  // TODO: заменить error на critical после расширения ролей action/critical.
  error: {
    main: css`
      background-color: ${colors.red[100]};
      color: ${colors.red[800]};
      ${counterStyles({
        color: colors.red[100],
        bg: colors.red[800],
      })};

      ${hover(css`
        background-color: ${colors.red[200]};
        ${counterStyles({
          color: colors.red[200],
        })};
      `)}

      ${selected(css`
        background-color: ${colors.red[600]};
        color: ${colors.white};
        ${counterStyles({
          color: colors.red[600],
          bg: colors.white,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.red[700]};
        ${counterStyles({
          color: colors.red[700],
        })};
      `)}

      ${focus(colors.gray[100])}
    `,
    ghost: css`
      background-color: transparent;
      color: ${colors.red[800]};
      ${counterStyles({
        color: colors.white,
        bg: colors.red[800],
      })};

      ${hover(css`
        background-color: ${colors.gray[200]};
        ${counterStyles({
          color: colors.gray[100],
        })};
      `)}

      ${selected(css`
        background-color: ${colors.red[100]};
        ${counterStyles({
          color: colors.red[100],
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.red[200]};
        ${counterStyles({
          color: colors.red[200],
        })};
      `)}

      ${focus(colors.red[100])}
    `,
  },
  super: {
    main: css`
      background-color: ${semantic.action.super.bg};
      color: ${semantic.text.onColored};
      ${counterStyles({
        color: semantic.action.super.bg,
        bg: semantic.text.onColored,
      })};

      ${hover(css`
        background-color: ${semantic.action.super.bgHover};
        ${counterStyles({
          color: semantic.action.super.bgHover,
        })};
      `)}

      ${focus(semantic.focus.ringNeutral)}
    `,
    secondary: css`
      background-color: ${semantic.action.super.bgSecondary};
      color: ${semantic.action.super.text};
      ${counterStyles({
        color: semantic.action.super.bgSecondary,
        bg: semantic.action.super.text,
      })};

      ${hover(css`
        background-color: ${semantic.action.super.bgSecondaryHover};
        ${counterStyles({
          color: semantic.action.super.bgSecondaryHover,
        })};
      `)}

      ${selected(css`
        background-color: ${semantic.action.super.bg};
        color: ${semantic.text.onColored};
        ${counterStyles({
          color: semantic.action.super.bg,
          bg: semantic.text.onColored,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${semantic.action.super.bgHover};
        ${counterStyles({
          color: semantic.action.super.bgHover,
        })};
      `)}

      ${focus(semantic.focus.ringNeutral)}
    `,
    // TODO: нужны роли обычного и hover-фона super/tertiary.
    tertiary: css`
      background-color: ${colors.gray[100]};
      color: ${semantic.action.super.text};
      ${counterStyles({
        color: colors.gray[100],
        bg: semantic.action.super.text,
      })};

      ${hover(css`
        background-color: ${colors.gray[200]};
        ${counterStyles({
          color: colors.gray[200],
        })};
      `)}

      ${selected(css`
        background-color: ${semantic.action.super.bgSecondary};
        ${counterStyles({
          color: semantic.action.super.bgSecondary,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${semantic.action.super.bgSecondaryHover};
        ${counterStyles({
          color: semantic.action.super.bgSecondaryHover,
        })};
      `)}

      ${focus(semantic.focus.ringSuper)}
    `,
    ghost: css`
      background-color: transparent;
      color: ${semantic.action.super.text};
      ${counterStyles({
        color: semantic.text.onColored,
        bg: semantic.action.super.text,
      })};

      ${hover(css`
        background-color: ${semantic.action.neutral.bgHover};
        ${counterStyles({
          color: semantic.action.neutral.bgHover,
        })};
      `)};

      ${selected(css`
        background-color: ${semantic.action.super.bgSecondary};
        ${counterStyles({
          color: semantic.action.super.bgSecondary,
        })};
      `)};

      ${selectedHover(css`
        background-color: ${semantic.action.super.bgSecondaryHover};
        ${counterStyles({
          color: semantic.action.super.bgSecondaryHover,
        })};
      `)};

      ${focus(semantic.focus.ringSuper)}
    `,
    // #586539: роли подписи и hover/selected для super/outline требуют уточнения.
    outline: css`
      background-color: ${semantic.action.super.bgOutline};
      color: ${colors.purple[800]};
      border-color: ${semantic.action.super.border};
      ${counterStyles({
        bg: semantic.action.super.bgOutline,
        color: colors.purple[800],
      })};

      ${hover(css`
        background-color: ${colors.gray[200]};
        ${counterStyles({
          color: colors.gray[200],
        })};
      `)};

      ${selected(css`
        background-color: ${colors.purple[100]};
        ${counterStyles({
          color: colors.purple[100],
        })};
      `)};

      ${selectedHover(css`
        background-color: ${colors.purple[200]};
        ${counterStyles({
          color: colors.purple[200],
        })};
      `)};

      ${focus(semantic.focus.ringSuper)}
    `,
  },
});
